// RESIZE a home widget by its corner (Nika 2026-09-27: "add resize
// functionality to widgets"; DESIGN-WL-HOME-WIDTH-RESIZE-1, HOME_WIDGETS_SPEC
// §1 dated line + §6).
//
// The board still knows four sizes and nothing else (S 1×1 · M 2×1 · L 2×2 ·
// XL the full row, functions/homeGridMetrics.js). A resize never invents a
// fifth: the pointer asks for a box, the box SNAPS to the nearest size this
// widget allows, and the release writes that size through the board's one
// durable layout write (`root._homeOps.setWidgetSize` → pages/main.js
// `_setHomeWidgetSize`: immediate, single-flight, outbox-backed).
//
//   the handle   — components/HomeWidgetResizeHandle.js (the frame child `HomeWidgetResizeHandle`), the
//                  card's bottom-right corner (shown on hover, focus-within and
//                  always in edit mode). Pointer down CAPTURES the pointer on
//                  the handle, so every move and the release land on it — no
//                  document listener; Escape on the (focused) handle cancels.
//   the outline  — `WidgetResizeGhost`, the dashed box the release will give
//                  (its own state: `{ size, w, h }`), painted over the
//                  neighbours from the card's top-left corner.
//   the minimum  — content sets the smallest size (HOME_AMBIENT_SKIN_SPEC §3
//                  law 2): the allowed sizes are the size picker's own rows
//                  (`homeWidgetSizeRows`), so the two can never disagree.
//   the keyboard — Shift + an arrow steps the focused widget one size that way
//                  in edit mode (functions/shortcutsHome.js), and the arrows on
//                  the focused handle do the same.
//
// `this` is an element (el.call) — the handle for the pointer / key handlers.
// Every export stands alone (frank serializes each one by itself): the shared
// reads are registered functions reached through `this.call`. The drag's
// bookkeeping is a plain handle on the root state (`root._homeResizing`, like
// the reorder's `root._homeDragging`) — one gesture at a time, never rendered.

// The sizes this widget may take, smallest first — the picker's rule.
export function homeWidgetResizeSizes(s) {
  const rows = this.call('homeWidgetSizeRows', s)
  const out = (Array.isArray(rows) ? rows : []).map((r) => r && r.value).filter(Boolean)
  return out.length ? out : ['s', 'm', 'l', 'xl']
}

// The box (px) and span a size takes at the grid `metrics` ({ cols, cell,
// gutter } — functions/homeGridMetrics.js `homeGridMetrics`). With the card's
// row `s`, a manifest `spanAt` override counts at its default size
// (homeWidgetSpanFor) — the outline is the box the card will really take.
export function homeWidgetResizeBox(size, metrics, s) {
  const m = metrics || {}
  const span = (s
    ? this.call('homeWidgetSpanFor', s, size, m.cols)
    : this.call('homeWidgetSpan', size, m.cols)) || { c: 1, r: 1 }
  const cell = Number(m.cell) || 0
  const gap = Number(m.gutter) || 0
  return {
    c: span.c,
    r: span.r,
    w: span.c * cell + (span.c - 1) * gap,
    h: span.r * cell + (span.r - 1) * gap
  }
}

// SNAP: the allowed size whose box is nearest to the wanted box `want`
// ({ w, h } in px, measured from the card's top-left corner). A tie keeps the
// `current` size — on one column S, M and XL are the same box, and a drag that
// changes nothing on screen must not change the saved size either — else the
// smaller size.
export function homeWidgetResizeSnap(want, metrics, allowed, current, s) {
  const sizes = Array.isArray(allowed) && allowed.length ? allowed : ['s', 'm', 'l', 'xl']
  const w = Number(want && want.w) || 0
  const h = Number(want && want.h) || 0
  let best = null
  let bestD = Infinity
  for (const size of sizes) {
    const box = this.call('homeWidgetResizeBox', size, metrics, s)
    const d = Math.hypot(box.w - w, box.h - h)
    if (d < bestD - 0.5) {
      best = size
      bestD = d
    } else if (Math.abs(d - bestD) <= 0.5 && size === current) {
      best = size
    }
  }
  return best || current || sizes[0]
}

// STEP: the next allowed size in an arrow's direction at `cols` columns, or
// null when there is none that way (a step that changes nothing on screen is
// no step). Right = wider, left = narrower, down = taller, up = shorter; among
// the sizes that move that way, the one nearest to one cell more / less.
export function homeWidgetResizeStep(current, dir, cols, allowed, s) {
  const sizes = Array.isArray(allowed) && allowed.length ? allowed : ['s', 'm', 'l', 'xl']
  const span = (z) =>
    (s ? this.call('homeWidgetSpanFor', s, z, cols) : this.call('homeWidgetSpan', z, cols)) || {
      c: 1,
      r: 1
    }
  const cur = span(current)
  const want = {
    c: cur.c + (dir === 'right' ? 1 : dir === 'left' ? -1 : 0),
    r: cur.r + (dir === 'down' ? 1 : dir === 'up' ? -1 : 0)
  }
  const ok = (sp) =>
    dir === 'right'
      ? sp.c > cur.c
      : dir === 'left'
        ? sp.c < cur.c
        : dir === 'down'
          ? sp.r > cur.r
          : dir === 'up'
            ? sp.r < cur.r
            : false
  let best = null
  let bestD = Infinity
  for (const z of sizes) {
    if (z === current) continue
    const sp = span(z)
    if (!ok(sp)) continue
    const d = Math.abs(sp.c - want.c) + Math.abs(sp.r - want.r)
    if (d < bestD) {
      best = z
      bestD = d
    }
  }
  return best
}

// The grid metrics a card sits in: its grid's own width through the one table
// (the same width the board's container queries read).
export function homeWidgetResizeMetrics(card) {
  const node = card && (card.node || card)
  const grid = node && node.parentElement
  const width = (grid && grid.clientWidth) || (node && node.getBoundingClientRect().width) || 0
  return this.call('homeGridMetrics', width)
}

// One keyboard step for the widget whose state row is `s` (`card` = its frame
// element): Shift + arrow (functions/shortcutsHome.js) and the arrows on the
// focused handle. True when the size changed.
export function homeWidgetResizeStepBy(s, dir, card) {
  const root = s && s.root
  const ops = root && root._homeOps
  if (!ops || typeof ops.setWidgetSize !== 'function' || !s.id) return false
  if (this.call('homeWidgetIsApp', s)) return false
  const metrics = this.call('homeWidgetResizeMetrics', card)
  const current = this.call('homeWidgetFrameSize', s)
  const next = this.call(
    'homeWidgetResizeStep',
    current,
    dir,
    metrics.cols,
    this.call('homeWidgetResizeSizes', s),
    s
  )
  if (!next || next === current) return false
  ops.setWidgetSize(s.id, next)
  return true
}

// POINTER DOWN on the handle (`this`): take the pointer, remember the card's
// box and the grid it sits in. Returns true when a resize started.
export function homeWidgetResizeStart(e, s) {
  const root = s && s.root
  const card = this && this.parent
  const ops = root && root._homeOps
  if (!root || !card || !card.node || !ops || typeof ops.setWidgetSize !== 'function') return false
  if (e && e.button !== undefined && e.button !== 0) return false
  if (root._homeDragging || this.call('homeWidgetIsApp', s)) return false
  if (e && typeof e.preventDefault === 'function') e.preventDefault()
  if (e && typeof e.stopPropagation === 'function') e.stopPropagation()
  const rect = card.node.getBoundingClientRect()
  const from = this.call('homeWidgetFrameSize', s)
  root._homeResizing = {
    id: s.id,
    pointerId: e && e.pointerId,
    left: rect.left,
    top: rect.top,
    // the pointer keeps its place relative to the corner it grabbed
    offX: rect.right - ((e && e.clientX) || 0),
    offY: rect.bottom - ((e && e.clientY) || 0),
    startX: (e && e.clientX) || 0,
    startY: (e && e.clientY) || 0,
    metrics: this.call('homeWidgetResizeMetrics', card),
    allowed: this.call('homeWidgetResizeSizes', s),
    from,
    size: from,
    moved: false
  }
  try {
    this.node.setPointerCapture(e.pointerId)
  } catch (_) {
    /* synthetic events carry no capturable pointer */
  }
  // Focus rides on the handle for the gesture: Escape lands here in every
  // browser (Safari does not focus a pressed button), and :focus-within keeps
  // the handle shown while the pointer leaves the card.
  try {
    this.node.focus({ preventScroll: true })
  } catch (_) {
    /* a detached node takes no focus */
  }
  return true
}

// POINTER MOVE: the wanted box follows the pointer; the outline shows the size
// it snaps to (a small dead zone keeps a click a click).
export function homeWidgetResizeMove(e, s) {
  const root = s && s.root
  const r = root && root._homeResizing
  if (!r || !s || r.id !== s.id) return false
  if (r.pointerId !== undefined && e && e.pointerId !== undefined && e.pointerId !== r.pointerId)
    return false
  if (!r.moved) {
    if (Math.hypot(e.clientX - r.startX, e.clientY - r.startY) < 4) return false
    r.moved = true
  }
  if (e.cancelable) e.preventDefault()
  const want = { w: e.clientX + r.offX - r.left, h: e.clientY + r.offY - r.top }
  const size = this.call('homeWidgetResizeSnap', want, r.metrics, r.allowed, r.size, s)
  const ghost = this.parent && this.parent.WidgetResizeGhost
  if (ghost && ghost.state && (size !== r.size || !r.shown)) {
    const box = this.call('homeWidgetResizeBox', size, r.metrics, s)
    ghost.state.update({ size, w: box.w, h: box.h })
    r.shown = true
  }
  r.size = size
  return true
}

// RELEASE (or cancel): drop the outline and the pointer; a changed size is
// written through the board's durable layout write. True when it wrote.
export function homeWidgetResizeEnd(e, s, cancelled) {
  const root = s && s.root
  const r = root && root._homeResizing
  if (!r || !s || r.id !== s.id) return false
  if (r.pointerId !== undefined && e && e.pointerId !== undefined && e.pointerId !== r.pointerId)
    return false
  root._homeResizing = null
  try {
    if (this.node && r.pointerId !== undefined) this.node.releasePointerCapture(r.pointerId)
  } catch (_) {
    /* already released */
  }
  const ghost = this.parent && this.parent.WidgetResizeGhost
  if (ghost && ghost.state && r.shown) ghost.state.update({ size: null, w: 0, h: 0 })
  if (cancelled || !r.moved || !r.size || r.size === r.from) return false
  const ops = root._homeOps
  if (!ops || typeof ops.setWidgetSize !== 'function') return false
  ops.setWidgetSize(s.id, r.size)
  return true
}

// KEYS on the focused handle: Escape cancels a resize in flight; an arrow
// (Shift or not) steps the size that way. The key is claimed (preventDefault +
// stopPropagation), so the board's Escape (leave edit mode) and the keyboard
// registry's Shift + arrow never run for the same keystroke.
export function homeWidgetResizeKey(e, s) {
  const root = s && s.root
  if (!root || !e) return false
  const r = root._homeResizing
  if (e.key === 'Escape' || e.key === 'Esc') {
    if (!r || r.id !== s.id) return false
    e.preventDefault()
    e.stopPropagation()
    this.call('homeWidgetResizeEnd', { pointerId: r.pointerId }, s, true)
    return true
  }
  const dir =
    e.key === 'ArrowRight'
      ? 'right'
      : e.key === 'ArrowLeft'
        ? 'left'
        : e.key === 'ArrowDown'
          ? 'down'
          : e.key === 'ArrowUp'
            ? 'up'
            : null
  if (!dir || r || e.metaKey || e.ctrlKey || e.altKey) return false
  e.preventDefault()
  e.stopPropagation()
  return this.call('homeWidgetResizeStepBy', s, dir, this.parent)
}

// The handle's hover title: "Widget size · M" (the size codes are symbols, the
// same in every language — the picker's own labels).
export function homeWidgetResizeTitle(s) {
  const label = this.call('polyglot', 'workspace.widget.size.label')
  const size = this.call('homeWidgetFrameSize', s)
  const code = { s: 'S', m: 'M', l: 'L', xl: 'XL' }[size] || 'S'
  return `${label} · ${code}`
}
